import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkle,
  CaretRight,
  CalendarBlank,
  Users,
  Barbell,
  X,
  ArrowRight,
} from '@phosphor-icons/react';
import { useAuth } from '../App';

const API = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});
API.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('pa_access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const dismissKey = (userId) => {
  // Local YYYY-MM-DD so the "dismiss for today" honours the user's timezone
  // (not UTC — otherwise a 23:30 CEST dismiss re-appears at 02:30 same night).
  const d = new Date();
  const day = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return `pa_recs_dismissed_${userId || 'anon'}_${day}`;
};

const RecommendationsWidget = ({ user }) => {
  const { language } = useAuth();
  const L = (en, de) => (language === 'de' ? de : en);
  const [matches, setMatches] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Respect once-per-day dismissal so we don't nag users who already saw it
    if (!user?.id) return;
    if (localStorage.getItem(dismissKey(user.id)) === '1') {
      setDismissed(true);
      setLoading(false);
      return;
    }
    let alive = true;
    (async () => {
      try {
        const [m] = await Promise.allSettled([
          API.get('/matches/recommended'),
          // AI trainer-slot suggestions are HIDDEN for now (Sep 2026 product
          // decision). Keeping the network call out entirely so we don't
          // waste round-trips. Re-enable by re-adding the /trainer-recommendations
          // call and setting `setTrainers(...)` from the response.
        ]);
        if (!alive) return;
        setMatches(m.status === 'fulfilled' ? (m.value.data?.matches || m.value.data || []).slice(0, 2) : []);
        setTrainers([]);
      } catch (_) {
        // silent — widget is optional
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [user?.id]);

  const handleDismiss = () => {
    localStorage.setItem(dismissKey(user?.id), '1');
    setDismissed(true);
  };

  if (dismissed || loading) return null;
  const hasMatch = matches.length > 0;
  const hasTrainer = trainers.length > 0;
  if (!hasMatch && !hasTrainer) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.35 }}
        className="mb-4 rounded-2xl border border-slate-200 bg-gradient-to-br from-emerald-50 via-white to-slate-50 shadow-sm overflow-hidden"
        data-testid="recommendations-widget"
      >
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-emerald-600 text-white flex items-center justify-center shadow-sm">
              <Sparkle size={15} weight="fill" />
            </div>
            <span className="text-sm font-bold text-slate-900">
              {L('For you', 'Für dich')}
            </span>
            <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5">
              {L('AI', 'KI')}
            </span>
          </div>
          <button
            onClick={handleDismiss}
            className="p-1 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600"
            aria-label={L('Dismiss', 'Ausblenden')}
            data-testid="recs-dismiss"
          >
            <X size={14} />
          </button>
        </div>

        <div className="p-3 grid gap-2.5 sm:grid-cols-2">
          {matches.map((m) => {
            const dateStr = m.date;
            const timeStr = m.start_time;
            const level = m.level_min ? `${m.level_min}-${m.level_max}` : (m.level ? m.level.toFixed(1) : '—');
            const spots = (m.max_players || 4) - (m.current_players?.length || 0);
            return (
              <Link
                key={m.id}
                to="/matches"
                className="group rounded-xl border border-emerald-200 bg-white hover:border-emerald-400 hover:shadow-md transition-all p-3 flex items-start gap-3"
                data-testid={`rec-match-${m.id}`}
              >
                <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                  <Users size={17} weight="bold" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase font-bold text-emerald-700 tracking-wide">
                    {L('Match on your level', 'Match auf deinem Level')}
                  </div>
                  <div className="text-sm font-bold text-slate-900 truncate mt-0.5">
                    {m.ai_headline || `${L('Level', 'Level')} ${level} · ${spots} ${L('spots free', 'Plätze frei')}`}
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5 flex items-center gap-1">
                    <CalendarBlank size={12} className="text-slate-400" />
                    {dateStr} · {timeStr}
                  </div>
                </div>
                <CaretRight size={16} className="text-slate-300 group-hover:text-emerald-500 mt-1 shrink-0" />
              </Link>
            );
          })}

          {trainers.map((t) => (
            <Link
              key={t.trainer_id || t.id || `${t.trainer_name}-${t.date}-${t.start_time}`}
              to="/book"
              className="group rounded-xl border border-amber-200 bg-white hover:border-amber-400 hover:shadow-md transition-all p-3 flex items-start gap-3"
              data-testid={`rec-trainer-${t.trainer_id || t.id}`}
            >
              <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                <Barbell size={17} weight="bold" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase font-bold text-amber-700 tracking-wide">
                  {L('Training available', 'Training verfügbar')}
                </div>
                <div className="text-sm font-bold text-slate-900 truncate mt-0.5">
                  {t.trainer_name || L('Coaching session', 'Trainerstunde')}
                </div>
                <div className="text-xs text-slate-600 mt-0.5 flex items-center gap-1">
                  {t.date && t.start_time ? (
                    <>
                      <CalendarBlank size={12} className="text-slate-400" />
                      {t.date} · {t.start_time}
                      {t.end_time && ` – ${t.end_time}`}
                    </>
                  ) : (
                    <span className="line-clamp-1">{t.ai_message || t.reason || L('Boost your level with a targeted session', 'Level-Boost mit einer gezielten Session')}</span>
                  )}
                </div>
              </div>
              <CaretRight size={16} className="text-slate-300 group-hover:text-amber-500 mt-1 shrink-0" />
            </Link>
          ))}
        </div>

        <div className="px-4 py-2 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between">
          <span className="text-[10px] text-slate-500">
            {L('Based on your level and rating trend', 'Basierend auf deinem Level und Rating-Trend')}
          </span>
          {hasMatch && (
            <Link
              to="/matches"
              className="text-[11px] font-semibold text-emerald-700 hover:text-emerald-800 flex items-center gap-0.5"
              data-testid="recs-see-all-matches"
            >
              {L('See all matches', 'Alle Matches')} <ArrowRight size={12} />
            </Link>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default RecommendationsWidget;
